/**
 * Modal/Dialog Accessibility
 */
import { safeSelect, safeSelectAll, announceForScreenReader } from './utils.js';

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const cachedFocusableElements = {};

/**
 * Initialisiert die Zugänglichkeit für Modals/Dialoge
 */
export const initModalAccessibility = (function() {
	// Private Variablen
	let dialogElements = [];
	
	/**
	 * Fokus innerhalb des Dialogs einfangen
	 * @param {Event} event - Keyboard-Event
	 * @param {HTMLElement} dialog - Dialog-Element
	 */
	function trapFocus(event, dialog) {
		try {
			if (!event || !dialog) return;
			
			const focusableElements = getFocusableElements(dialog);
			
			if (focusableElements.length === 0) return;
			
			const firstElement = focusableElements[0];
			const lastElement = focusableElements[focusableElements.length - 1];
			
			// Shift+Tab von erstem Element: zum letzten Element springen
			if (event.shiftKey && document.activeElement === firstElement) {
				event.preventDefault();
				lastElement.focus();
			} 
			// Tab von letztem Element: zum ersten Element springen
			else if (!event.shiftKey && document.activeElement === lastElement) {
				event.preventDefault();
				firstElement.focus();
			}
		} catch (error) {
			console.error('Fehler beim Einfangen des Fokus:', error);
		}
	}
	
	/**
	 * Initialisiert den Dialog mit Tastatursteuerung und Fokus-Management
	 * @param {HTMLElement} dialog - Das zu initialisierende Dialog-Element
	 */
	function setupDialog(dialog) {
		if (!dialog) return;
		
		try {
			// Keyboard-Trap für Modals
			dialog.addEventListener('keydown', function(event) {
				// Wenn der Dialog nicht sichtbar ist, nichts tun
				if (dialog.getAttribute('aria-hidden') === 'true') {
					return;
				}
				
				// ESC-Taste schließt Dialog
				if (event.key === 'Escape') {
					const closeButton = safeSelect('.navigation-toggle--close', dialog);
					if (closeButton) {
						closeButton.click();
						return;
					}
				}
				
				// Wenn Tab gedrückt wird
				if (event.key === 'Tab') {
					trapFocus(event, dialog);
				}
			});
			
			// Status-Änderungen überwachen
			const observer = new MutationObserver((mutations) => {
				mutations.forEach((mutation) => {
					if (mutation.type === 'attributes' && mutation.attributeName === 'aria-hidden') {
						const isHidden = dialog.getAttribute('aria-hidden') === 'true';
						
						// Cache beim Öffnen zurücksetzen, um neue Elemente zu erfassen
						if (!isHidden) {
							const cacheKey = dialog.id || 'dialog';
							delete cachedFocusableElements[cacheKey];
							
							// Erstes fokussierbares Element fokussieren
							setTimeout(() => {
								const elements = getFocusableElements(dialog);
								if (elements.length > 0) {
									elements[0].focus();
								}
							}, 10);
						}
					}
				});
			});
			
			observer.observe(dialog, { attributes: true });
		} catch (error) {
			console.error('Fehler beim Setup des Dialogs:', error);
		}
	}
	
	// Öffentliche Schnittstelle
	return function() {
		try {
			dialogElements = safeSelectAll('[role="dialog"][aria-modal="true"]');
			
			dialogElements.forEach(dialog => {
				if (dialog) {
					setupDialog(dialog);
				}
			});
		} catch (error) {
			console.error('Fehler bei der Initialisierung der Modal-Zugänglichkeit:', error);
		}
	};
})();

/**
 * Holt alle fokussierbaren Elemente eines Containers
 * @param {HTMLElement} element - Container-Element
 * @returns {Array} - Array mit fokussierbaren Elementen
 */
export function getFocusableElements(element) {
	if (!element) return [];
	
	try {
		// Cache-Schlüssel generieren
		const cacheKey = element.id || 'element-' + Math.random().toString(36).substr(2, 9);
		
		// Cache prüfen
		if (!cachedFocusableElements[cacheKey] || document.activeElement === document.body) {
			// Nur neu selektieren, wenn nötig
			cachedFocusableElements[cacheKey] = safeSelectAll(FOCUSABLE_SELECTOR, element)
				.filter(el => {
					// Prüfen, ob Element wirklich sichtbar ist
					return window.getComputedStyle(el).display !== 'none' && 
							window.getComputedStyle(el).visibility !== 'hidden';
				});
		}
		
		return cachedFocusableElements[cacheKey];
	} catch (error) {
		console.error('Fehler beim Holen fokussierbarer Elemente:', error);
		return [];
	}
}